import { List, type RecordOf } from 'immutable';

// The helpers in this file are intended to smooth over the rough edges of "do I have an immutable value or not"

type ImmutableLike = object & { toJS(): unknown };

function isImmutableLike(value: object): value is ImmutableLike {
  return 'toJS' in value && typeof (value as ImmutableLike).toJS === 'function';
}

/**
 * If `value` exposes Immutable-style `toJS()`, returns that deep plain snapshot.
 * Otherwise returns `value` unchanged (already plain, or an object without `toJS`).
 *
 * Use at UI / Formik boundaries when the same field may be an Immutable `Record` or a POJO.
 * `null` and `undefined` pass through unchanged.
 */
export function toPlainIfImmutable<T>(value: T | null | undefined): T | null | undefined {
  if (value == null) return value;
  if (typeof value !== 'object') return value;
  if (isImmutableLike(value)) {
    return value.toJS() as T;
  }
  return value;
}

export const getAt = <T>(collection: List<T> | ReadonlyArray<T>, index: number): T | undefined =>
  List.isList(collection) ? collection.get(index) : collection[index];

export const reduce = <T, R>(
  collection: List<T> | ReadonlyArray<T>,
  start: R,
  accumulator: (result: R, item: T) => R,
): R => (List.isList(collection) ? collection.reduce(accumulator, start) : collection.reduce(accumulator, start));

export const ensureArray = <T>(collection: List<T> | Array<T>): Array<T> =>
  List.isList(collection) ? collection.toArray() : collection;

export const ensureJsArray = <T extends object>(
  collection: List<T> | List<RecordOf<T>> | Array<T> | undefined | null,
): Array<T> => (List.isList(collection) ? (collection?.toJS() as Array<T>) : (collection ?? []));

export const getLength = <T>(collection: List<T> | Array<T> | null | undefined): number => {
  if (!collection) return 0;

  return List.isList(collection) ? collection.size : collection.length;
};
